import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';

interface WalletSecurityBannerProps {
  walletSecurity?: {
    has_exported_key: boolean;
    should_show_reminder: boolean;
  };
  onDismiss?: () => void;
}

const WalletSecurityBanner: React.FC<WalletSecurityBannerProps> = ({ 
  walletSecurity,
  onDismiss 
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  useEffect(() => {
    // Show banner if user should see the reminder
    if (walletSecurity?.should_show_reminder) {
      setIsVisible(true);
    }
  }, [walletSecurity]);

  const handleDismiss = async () => {
    setIsDismissing(true);
    try {
      await apiService.wallet.dismissSecurityReminder();
      setIsVisible(false);
      onDismiss?.();
    } catch (error) {
      console.error('Failed to dismiss security reminder:', error);
    } finally {
      setIsDismissing(false);
    }
  };

  const handleExportClick = () => {
    navigate('/settings');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-500/30 rounded-xl p-4 mb-4"
      >
        <div className="flex items-start gap-3">
          {/* Warning Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <svg 
              className="w-6 h-6 text-yellow-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-yellow-300 font-semibold text-sm mb-1">
              Secure Your Wallet
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              Your wallet is encrypted with your password. 
              <span className="text-yellow-200 font-medium"> If you lose your password, your funds cannot be recovered.</span>
              {' '}System backups cannot help without your password. Export your private key to ensure you always have access to your funds.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={handleExportClick}
                className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 
                         text-yellow-300 text-sm font-medium rounded-lg transition-colors
                         flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Export Private Key
              </button>
              <button
                onClick={handleDismiss}
                disabled={isDismissing}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDismissing ? 'Dismissing...' : 'Remind me later'}
              </button>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={handleDismiss}
            disabled={isDismissing}
            className="flex-shrink-0 text-gray-400 hover:text-gray-300 transition-colors
                     disabled:opacity-50"
            aria-label="Dismiss"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WalletSecurityBanner;
